// ANGULAR
import { ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnInit, Output, signal } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

// Rxjs
import { map } from 'rxjs/operators';

// SERVICE
import { ClientStoreService } from '../../../../../state/modules/client/client-store.service';
import { EmployeeStoreService } from '../../../../../state/modules/employee/employee-store.service';

// CONSTANTS
import { CLIENT_METHOD_CONSTANT } from '../../../../../constants/ngrx-store/client';
import { API_METHOD_CONSTANTS } from '../../../../../constants/ngrx-store/core';
import { EMPLOYEE_METHOD_CONSTANT } from '../../../../../constants/ngrx-store/employee';
import { ASSIGN_LOCATION_FORM_CONTROLS, CUSTOM_SAVE_BUTTON_CONFIG, noRecordFound } from './assign-location.constant';

// LODASH
import { groupBy } from 'lodash';
import { PERMISSION_CONSTANT } from '../../../../../constants/permission';
import { LocalStorageUtil } from '../../../../../core/utils/local-storage-util';
import { ModalBaseClass } from '../../../../shared/components/modals/base_class/modal-base-class';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeleteErrorListComponent } from '../../../../standalone/components/delete-error-list/delete-error-list.component';
import { UserService } from '../../../../../state/modules/user/services/user.service';
import { EmployeeService } from '../../../../../state/modules/employee/services/employee.service';
import { PERMISSIONV2 } from '../../../../../constants/permissionv2/role-permission';
import { MasterService } from '../../../../../state/master/services/master.service';

@Component({
  standalone: false,
  selector: 'kt-assign-location',
  templateUrl: './assign-location.component.html',
  styleUrls: ['./assign-location.component.scss'],
})
export class AssignLocationComponent extends ModalBaseClass implements OnInit {
  description = null;
  isModal = false;
  noRecordFound = noRecordFound ;
  PermissionV2 = PERMISSIONV2
  masterBusinessLabel = MasterService.masterBusinessLabel

  /**
      * External inputs
      */
  @Input() options;
  @Input() set employeeData(value) {
    if (value) {
      this._employeeData = value;
      this.setLoggedInUserProfile();
      if (this._employeeData && this._employeeData.employee_id && this.options && !this.options.showCancel) {
        this.loading.set(true);
        setTimeout(() => {
          this.dispatchGetAssignLocationByEmployee(this._employeeData.employee_fk_user_id);
        }, 1500);
      }
    }
  }
  @Input() set assignLocationForm(value) {
    this._assignLocationForm = value ? value : new UntypedFormGroup({ assignlocations: new UntypedFormArray([]) });
  }

  /**
 * External outputs
 */
  @Output() callback = new EventEmitter<any>();

  /**
   * Constants
   */
  formConstants = ASSIGN_LOCATION_FORM_CONTROLS;
  buttonConstants = CUSTOM_SAVE_BUTTON_CONFIG;
  _assignLocationForm: UntypedFormGroup;
  _employeeData = null;
  lookupOptions: any = {
    locations: {},
    client: [],
  };
  loading = signal(false);
  assignLocationData = [];
  previousSelectedIds = [];
  isLoggedInUserProfile = false;
  permissionConstant = PERMISSION_CONSTANT;
  constructor(private employeeStoreService: EmployeeStoreService,
    public clientStoreService: ClientStoreService,
    public employeeService: EmployeeService,
    public userService: UserService,
    private ngbModal: NgbModal,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private fb: UntypedFormBuilder) {
    super(ngbModal);
  }


  setLoggedInUserProfile() {
    const loginUserId = LocalStorageUtil.getUserId();
    if (loginUserId === this._employeeData.employee_fk_user_id) {
      this.isLoggedInUserProfile = true;
    } else {
      this.isLoggedInUserProfile = false;
    }
  }

  ngOnInit() {
    this.refreshBusinessLabels();
    this.dispatchClient();
    this.setLookupData();
  }

  private refreshBusinessLabels() {
    const labels = MasterService.masterBusinessLabel;
    if (this.formConstants?.client?.templateOptions) {
      this.formConstants.client.templateOptions.label = labels.Client;
    }
    if (this.formConstants?.location?.templateOptions) {
      this.formConstants.location.templateOptions.label = labels.Location;
    }
    if (this.buttonConstants?.options && !Array.isArray(this.buttonConstants.options)) {
      this.buttonConstants.options.text = 'Save Assign ' + labels.Location;
    }
  }

  // ----------------- Begin: Selectors --------------- //

  /**
   * Set Dropdown options for client
   */
  setLookupData() {
    this.lookupOptions.client[0] = this.clientStoreService.selectClientList();
  }

  /**
   * api call on client dropdown value change or set null
   */
  clientChange(event, index) {
    this.setOptions();
    if (event && event.value) {
      this.dispatchClientLocation(event.value);
      this.getAssignLocationFormArray().controls[index].get('location').enable();
      this.getAssignLocationFormArray().controls[index].get('location').setValue(null);
    } else {
      this.getAssignLocationFormArray().controls[index].get('location').setValue(null);
      this.getAssignLocationFormArray().controls[index].get('location').disable();
    }
  }

  // ----------------- End: Selectors --------------- //

  /**
*  To get the assignLocation formArray
*/
  getAssignLocationFormArray(): UntypedFormArray {
    return this._assignLocationForm.controls.assignlocations as UntypedFormArray;
  }

  /**
* To add new assignLocation
*/
  addAssignLocation() {
    this.getAssignLocationFormArray().push(new UntypedFormGroup({ employee_client_location_id: new UntypedFormControl(0) }));
    this.cdr.markForCheck();
    setTimeout(() => {
      this.setOptions();
      this.cdr.markForCheck();
    }, 10);
  }

  /**
 * To remove assignLocation at given index
 * @param index
 */
  removeAssignLocation(index) {
    this.getAssignLocationFormArray().removeAt(index);
    this.cdr.markForCheck();
    setTimeout(() => {
      this.setOptions();
      this.cdr.markForCheck();
    }, 10);
  }

  /**
   * To get the assignLocation formgroup at given assignLocationIndex
   * @param assignLocationIndex
   */
  getAssignLocation(assignLocationIndex: number): UntypedFormGroup {
    return this.getAssignLocationFormArray().controls[assignLocationIndex] as UntypedFormGroup;
  }

  /**
 * Set the form for client location data
 */
  setAssignClientLocationData() {
    const formatedData = [];
    const groupByData = groupBy(this.assignLocationData, 'employee_client_location_fk_client_id');
    Object.keys(groupByData).forEach(object => {
      const obj = { client: parseInt(object), location: groupByData[object].map(location => location.employee_client_location_fk_location_id) };
      formatedData.push(obj);
    });
    // employeeData Input  Set Event multiple time call at that time multiple times added formArray Control so i added this if condition. if (formatedData.length > this.getAssignLocationFormArray().length)
    if (formatedData.length > this.getAssignLocationFormArray().length) {
      for (const val of formatedData) {
        this.addAssignLocation();
        setTimeout(() => {
          this.dispatchClientLocation(val.client);
        }, 500);
      }
    }

    if (!formatedData.length) {
      // this.addAssignLocation();
    }

    setTimeout(() => {
      this._assignLocationForm.get('assignlocations').patchValue(formatedData);
      this.setOptions();
    }, 500);
  }

  /**
* save the assign location
*/
  saveAssignLocation() {
    if (this._assignLocationForm.invalid) {
      setTimeout(() => {
        this._assignLocationForm.markAllAsTouched();
      });

      return;
    }

    this.loading.set(true);
    const payload: any = {};
    let currentClientLocationData = [];
    const formData = this._assignLocationForm.value;
    (formData.assignlocations).forEach(element => {
      const clientId = element.client;
      const locations = element.location;
      const clientLocationId = element.employee_client_location_id;
      locations.forEach(element => {
        const obj: any = {};
        obj.employee_client_location_fk_employee_id = this._employeeData.employee_id;
        obj.employee_client_location_fk_client_id = clientId;
        obj.employee_client_location_fk_location_id = element;
        obj.employee_client_location_fk_user_id = this._employeeData.employee_fk_user_id;
        obj.employee_client_location_id = clientLocationId;
        currentClientLocationData.push(obj);
      });
    });


    let new_locations = [];
    const delete_locations = [];
    const existing_locations = [];
    if (!this.assignLocationData) {
      this.assignLocationData = [];
    }
    if (!currentClientLocationData.length) {
      currentClientLocationData = [];
    }
    if (this.assignLocationData.length) {
      currentClientLocationData.forEach(element => {
        const index = this.assignLocationData.findIndex(s => s.employee_client_location_fk_client_id === element.employee_client_location_fk_client_id &&
          s.employee_client_location_fk_location_id === element.employee_client_location_fk_location_id);
        if (index > -1) {
          existing_locations.push(currentClientLocationData[index]);
        } else {
          new_locations.push(element);
        }
      });

      this.assignLocationData.forEach(element => {
        const index = currentClientLocationData.findIndex(s => s.employee_client_location_fk_client_id === element.employee_client_location_fk_client_id &&
          s.employee_client_location_fk_location_id === element.employee_client_location_fk_location_id);
        if (index > -1) {
          // already added to existing so no need to add again
        } else {
          delete_locations.push(element);
        }
      });
    } else {
      new_locations = currentClientLocationData;
    }

    payload.add = new_locations;
    payload.update = [];
    payload.delete = delete_locations;
    this.dispatchAssignEmployee(payload)
  }




  dispatchAssignEmployee(payload, force = false) {
    payload.is_force_delete = force;
    this.employeeStoreService.dispatchEmployeeCommon({
      data: payload,
      module: EMPLOYEE_METHOD_CONSTANT.EMPLOYEE_ASSIGN_LOCATION,
      method: API_METHOD_CONSTANTS.UPDATE,
      callback: this.assignLocationCallback.bind(this, payload),
    });
  }
  /**
  * Callback of update assign location api
  */
  assignLocationCallback(lastCallPayload, data) {
    this.loading.set(false);
    this._assignLocationForm.markAsPristine();
    // if (this.userService.gettingStartedBusinessValue()) {
    //   this.userService.gettingStartedBusiness();
    // }

    if (data.success) {
      if (data && data.response && data.response.employee_client_location && data.response.employee_client_location.deleteResponseModels && data.response.employee_client_location.deleteResponseModels.length) {
        const modalRef = this.openPopUp(DeleteErrorListComponent, { data: data.response.employee_client_location.deleteResponseModels[0].delete_response },
          { centered: true, size: 'lg', keyboard: true, backdrop: 'static' },
        );
        modalRef.result.then(res => {
          if (res && res.force) {
            this.dispatchAssignEmployee(lastCallPayload, true);
          } else {
            this.getAssignLocationFormArray().clear();
            this.cdr.markForCheck();
            this.dispatchGetAssignLocationByEmployee(this._employeeData.employee_fk_user_id);
          }
        })
      } else {
        this.assignLocationData = data.response.employee_client_location.employee_client_locations;
        this.employeeService.refreshAssignTimesheet$.next(true);
        if (this.isModal) {
          const activeModal = this.injector.get(NgbActiveModal);
          activeModal.close({});
        }
      }
    }

  }

  /**
   * To make
   */
  setOptions() {
    if (this._assignLocationForm.value && this._assignLocationForm.value.assignlocations) {
      const selectedClients: Array<any> = this._assignLocationForm.value.assignlocations.map(value => value.client);
      this._assignLocationForm.value.assignlocations.forEach((assignLocation, index) => {
        // All selected client except client at given index
        const filteredClient = selectedClients.filter((client, i) => i !== index);
        this.lookupOptions.client[index] = this.clientStoreService.selectClientList().pipe(
          map(clients => clients ? clients.filter(client => !filteredClient.includes(client.client_id)) : []),
        );
      });
    }
  }


  /**
 * Callback to handle the responsea of get by employee id
 */
  setEmployeeClientLocationData(data) {
    this.loading.set(false);
    if (data.success) {
      this.assignLocationData = data.data.employee_client_location;
      this.setAssignClientLocationData();

    }
  }
  // ----------------- Begin: Dispatch --------------- //

  /**
* Dispatch client lookup
*/
  dispatchClient() {
    setTimeout(() => {
      this.clientStoreService.dispatchClientCommon({
        module: CLIENT_METHOD_CONSTANT.CLIENT,
        method: API_METHOD_CONSTANTS.GET_LOOKUP,
      });
    }, 1000);
  }

  /**
* Dispatch client-location data by employee id
*/
  dispatchGetAssignLocationByEmployee(id) {
    this.loading.set(true);
    this.employeeStoreService.dispatchEmployeeCommon({
      data: { userId: id },
      module: EMPLOYEE_METHOD_CONSTANT.EMPLOYEE_ASSIGN_LOCATION,
      method: API_METHOD_CONSTANTS.GET_BY_ID,
      callback: this.setEmployeeClientLocationData.bind(this),
    });
  }

  /**
 * Dispatch location list by client id
 */
  dispatchClientLocation(clientId) {
    this.clientStoreService.dispatchGetLocationListByClient({ data: { client_id: clientId } });
    this.lookupOptions.locations[clientId] = this.clientStoreService.selectClientLocationList(clientId);

  }

  // ----------------- End: Dispatch --------------- //

}
